import React from 'react';
import { motion } from 'framer-motion';

const DocCard = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay }}
    className="w-full rounded-3xl border border-gray-200 bg-white shadow-sm p-8 lg:p-10"
  >
    {children}
  </motion.div>
);

const Documentation = () => {
  return (
    <main className="min-h-screen bg-white text-gray-900">
      {/* Prominent Hero: central mesh (variant) */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 pointer-events-none">
          {/* New motion graphic: concentric rings with orbiting nodes and curved links */
          /* Distinct from other pages, but on-brand */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.6 }} transition={{ duration: 1 }} className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[900px]">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                <linearGradient id="ring-grad" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
              </defs>
              {[18, 28, 38].map((r, i) => (
                <circle key={r} cx="50" cy="50" r={r} fill="none" stroke="url(#ring-grad)" strokeWidth="0.6" opacity="0.35">
                  <animate attributeName="r" values={`${r-2};${r};${r-2}`} dur={`${5 - i*0.7}s`} repeatCount="indefinite" />
                </circle>
              ))}
              {/* Orbiting nodes */}
              {Array.from({ length: 6 }).map((_, i) => (
                <g key={i}>
                  <animateTransform attributeName="transform" attributeType="XML" type="rotate" from={`0 50 50`} to={`360 50 50`} dur={`${10 + i}s`} repeatCount="indefinite" />
                  <circle cx={50 + 28} cy="50" r="1.2" fill="#60a5fa" opacity="0.9" />
                  <circle cx={50 + 18} cy="50" r="0.9" fill="#a78bfa" opacity="0.8" />
                </g>
              ))}
              {/* Curved bezier links */}
              {Array.from({ length: 5 }).map((_, i) => (
                <path key={i} d={`M ${50 - 28 + i*6},${50 - 10} C 50,${40 - i*2} 50,${60 + i*2} ${50 + 28 - i*6},${50 + 10}`} stroke="#93c5fd" strokeWidth="0.2" opacity="0.3" fill="none">
                  <animate attributeName="opacity" values="0.15;0.4;0.15" dur={`${2 + i*0.3}s`} repeatCount="indefinite" />
                </path>
              ))}
            </svg>
          </motion.div>
        </div>
        <div className="container-custom py-24 lg:py-32 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold font-slim tracking-tight">FlowMatic — Documentation (For Clients)</h1>
          </motion.div>
        </div>
      </section>

      <section className="container-custom py-16 lg:py-20">
        <div className="max-w-5xl mx-auto space-y-8">
          <DocCard>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Overview</span>
            </h2>
            <p className="text-lg leading-relaxed text-gray-700">
              Welcome to FlowMatic Documentation — your complete guide to managing and optimizing your AI agents. We build and deploy intelligent automation systems tailored to your business. This documentation helps you understand how your agent works and how we keep it performing at its best.
            </p>
          </DocCard>

          <DocCard delay={0.05}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Getting Started</span>
            </h2>
            <div className="space-y-6 text-gray-700">
              <div>
                <h3 className="font-semibold">Step 1 — Discovery Call</h3>
                <p>We begin by understanding your goals, workflows, and communication systems.</p>
              </div>
              <div>
                <h3 className="font-semibold">Step 2 — Development & Customization</h3>
                <p>Our team designs and builds your AI agent — fully customized for your company’s tone, data, and automation requirements.</p>
              </div>
              <div>
                <h3 className="font-semibold">Step 3 — Deployment</h3>
                <p>After testing and approval, we deploy your AI agent on your preferred platform (website, CRM, or phone system).</p>
              </div>
              <div>
                <h3 className="font-semibold">Step 4 — Optimization</h3>
                <p>We analyze early performance data and fine-tune the agent for better accuracy and efficiency.</p>
              </div>
            </div>
          </DocCard>

          <DocCard delay={0.1}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Managing Your Agent</span>
            </h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold">Monitoring</h3>
                <p>We actively monitor your agent’s performance to ensure accuracy, responsiveness, and reliability.</p>
              </div>
              <div>
                <h3 className="font-semibold">Updates</h3>
                <p>Any time your services, pricing, or operations change, we update your AI agent’s knowledge base.</p>
              </div>
              <div>
                <h3 className="font-semibold">Reports</h3>
                <p>You’ll receive detailed performance reports, including metrics like conversations handled, leads generated, and bookings made.</p>
              </div>
            </div>
          </DocCard>

          <DocCard delay={0.15}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Troubleshooting</span>
            </h2>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold">Common Issues</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><span className="font-medium">Agent not responding:</span> Please check your network or hosting connection.</li>
                  <li><span className="font-medium">Outdated information:</span> Request a refresh or retraining of your agent’s knowledge base.</li>
                  <li><span className="font-medium">Lag or delay:</span> Contact our support to review system performance.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold">Support Contact</h3>
                <p>📧 Email: support@flowmatic.com</p>
              </div>
            </div>
          </DocCard>

          <DocCard delay={0.2}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Data & Security</span>
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>We take data security seriously.</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All client data is encrypted and handled with strict confidentiality.</li>
                <li>We never store or use your business data for model training without written consent.</li>
                <li>Each client’s agent operates in a secure, isolated environment.</li>
              </ul>
            </div>
          </DocCard>

          <DocCard delay={0.25}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Maintenance & Updates</span>
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We provide continuous maintenance to ensure your AI agent evolves with your business.</li>
              <li>Regular retraining with new data.</li>
              <li>Integration updates with your connected systems.</li>
              <li>Real-time performance and reliability monitoring.</li>
            </ul>
          </DocCard>

          <DocCard delay={0.3}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Customization Requests</span>
            </h2>
            <p className="text-gray-700 mb-2">Want to expand your agent’s capabilities? We can add new features such as:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Custom workflows and automation triggers.</li>
              <li>Integrations with CRMs, calendars, and databases.</li>
              <li>Personality or tone adjustments to match your brand.</li>
            </ul>
          </DocCard>

          <DocCard delay={0.35}>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 text-center">
              <span className="inline-block px-8 py-2 rounded-2xl bg-gradient-to-r from-gray-300/30 via-gray-400/30 to-gray-300/30 shadow-sm">Contact Support</span>
            </h2>
            <p className="text-gray-700">For any issues, updates, or new requests, reach out anytime.</p>
            <p className="mt-2 font-medium">📧 support@flowmatic.com</p>
          </DocCard>
        </div>
      </section>
    </main>
  );
};

export default Documentation;


